import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import { DataConnectionStatus } from '@nimmsta/ng-commons-lib/models';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import {
  FileSelectEvent,
  FileUpload,
  FileUploadModule,
} from 'primeng/fileupload';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';
import { finalize, Subscription } from 'rxjs';
import { DataConnectionService } from '../../../services/dataConnection.service';
import {
  isSortingImportPending,
  SortingDataConnection,
  SortingImportResponse,
  SortingUploadRequest,
} from '../../../models/sortingDataConnection';
import { readSortingUpload, SortingFileError } from './sorting-upload-file';

@Component({
  selector: 'app-sorting-upload',
  templateUrl: './sorting-upload.component.html',
  imports: [
    TranslatePipe,
    ButtonModule,
    FileUploadModule,
    MessageModule,
    ProgressBarModule,
  ],
})
export class SortingUploadComponent implements OnInit {
  readonly modalRef = inject(DynamicDialogRef);
  private readonly config = inject(
    DynamicDialogConfig<{ dataConnection: SortingDataConnection }>,
  );
  private readonly service = inject(DataConnectionService);
  private readonly authorization = inject(AuthorizationService);
  private readonly destroyRef = inject(DestroyRef);
  readonly connection = this.config.data!.dataConnection;
  readonly fileName = signal('');
  readonly preparing = signal(false);
  readonly uploading = signal(false);
  readonly polling = signal(false);
  readonly status = signal<SortingImportResponse | undefined>(undefined);
  readonly errorKey = signal<string | undefined>(undefined);
  readonly request = signal<SortingUploadRequest | undefined>(undefined);
  private pollSubscription?: Subscription;
  private selection = 0;

  get allowed(): boolean {
    return (
      this.connection.status === DataConnectionStatus.ACTIVE &&
      this.authorization.hasPermission('job.upload')
    );
  }

  get pending(): boolean {
    const status = this.status();
    return !!status && isSortingImportPending(status.status);
  }

  get progress(): number {
    const status = this.status();
    return status?.total
      ? Math.min(100, Math.round((100 * status.processed) / status.total))
      : 0;
  }

  get failureKey(): string {
    const known = [
      'invalid-file',
      'processing-failed',
      'import-interrupted',
      'import-expired',
    ];
    const code = this.status()?.errorCode ?? '';
    return `dataconnection.sorting.errors.${known.includes(code) ? code : 'processing-failed'}`;
  }

  ngOnInit(): void {
    const previous = this.service.lastSortingImport(this.connection.id);
    if (previous) {
      this.status.set(previous);
      if (this.allowed && isSortingImportPending(previous.status)) {
        this.watch(previous);
      }
    }
  }

  async selectFile(event: FileSelectEvent, picker: FileUpload): Promise<void> {
    const file = event.currentFiles[0];
    picker.clear();
    if (!file || !this.allowed || this.uploading() || this.pending) {
      return;
    }
    const selection = ++this.selection;
    this.request.set(undefined);
    this.status.set(undefined);
    this.errorKey.set(undefined);
    this.fileName.set(file.name);
    this.preparing.set(true);
    try {
      const request = await readSortingUpload(
        file,
        this.connection.configuration,
        crypto.randomUUID(),
      );
      if (selection === this.selection && !this.destroyRef.destroyed) {
        this.request.set(request);
      }
    } catch (error) {
      if (selection === this.selection && !this.destroyRef.destroyed) {
        this.errorKey.set(
          error instanceof SortingFileError
            ? error.translationKey
            : 'dataconnection.sorting.errors.fileRead',
        );
      }
    } finally {
      if (selection === this.selection && !this.destroyRef.destroyed) {
        this.preparing.set(false);
      }
    }
  }

  upload(): void {
    const request = this.request();
    if (
      !request ||
      !this.allowed ||
      this.uploading() ||
      this.preparing() ||
      this.pending
    ) {
      return;
    }
    this.errorKey.set(undefined);
    this.uploading.set(true);
    this.service
      .uploadSortingData(this.connection.id, request)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.uploading.set(false)),
      )
      .subscribe({
        next: (status) => this.watch(status),
        error: (error: HttpErrorResponse) => {
          this.errorKey.set(
            `dataconnection.sorting.errors.${error.status === 409 ? 'conflict' : error.status === 403 ? 'permission' : error.status === 413 ? 'fileTooLarge' : error.status === 422 ? 'invalid-file' : 'uploadFailed'}`,
          );
        },
      });
  }

  resumeProgress(): void {
    const status = this.status();
    if (status && this.allowed && !this.polling()) {
      this.watch(status);
    }
  }

  resubmit(): void {
    const request = this.request();
    const status = this.status();
    if (
      request &&
      (status?.status === 'FAILED' || status?.status === 'CANCELLED')
    ) {
      // Deliberate resubmission creates a new job; a transport retry keeps the same ID.
      this.request.set({ ...request, uploadId: crypto.randomUUID() });
      this.status.set(undefined);
      this.upload();
    }
  }

  private watch(initial: SortingImportResponse): void {
    this.pollSubscription?.unsubscribe();
    this.errorKey.set(undefined);
    this.polling.set(true);
    this.pollSubscription = this.service
      .watchSortingImport(this.connection.id, initial)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.polling.set(false)),
      )
      .subscribe({
        next: (status) => {
          this.status.set(status);
          if (status.status === 'COMPLETED') {
            this.request.set(undefined);
          }
        },
        error: (error: HttpErrorResponse) => {
          const unavailable = [403, 404, 422].includes(error.status);
          this.errorKey.set(
            `dataconnection.sorting.errors.${unavailable ? 'unavailable' : 'progressFailed'}`,
          );
        },
      });
  }
}
